import type { DatePickerProps } from '@mfw/components';

import { fromCivilDate, isCivilDate, toCivilDate } from '@mfw/common';
import { DatePicker, get } from '@mfw/components';
import { type FieldError, Controller } from 'react-hook-form';

import { withInputTestId } from '../../utils/withInputTestId';

/**
 * The date picker for CIVIL fields ("date of birth", "valid from" — a
 * calendar day with no time and no zone). The form state, zod schema and
 * wire all carry a bare 'yyyy-MM-dd' string; the JS `Date` the underlying
 * MUI picker needs exists only INSIDE this component, so application code
 * never touches it and cannot mis-serialize it. Use this instead of
 * `FormDatePicker` for every date-only field (see the date-handling policy).
 */
// `Date`/`boolean`, not a `TDate extends Date` generic. `@mfw/components`
// exports its picker as `React.forwardRef(DatePickerWrapper)`, and `forwardRef`
// ERASES a component's type parameters — the exported `DatePicker` only ever
// accepts `DatePickerProps<Date, boolean>`. A wrapper generic over `TDate` was
// therefore unsatisfiable (a `TDate` could be a narrower subtype of `Date`,
// which the fixed component cannot accept), and that is the whole content of
// the `@ts-ignore` that used to sit on the element below. No call site ever
// supplied a type argument.
export interface FormCivilDatePickerProps extends Omit<
  DatePickerProps<Date, boolean>,
  'defaultValue' | 'name' | 'onChange' | 'value' | 'variant'
> {
  name: string;
  /**
   * Final review M4 — MUI X does NOT forward unknown props to the text field
   * it renders, so a `data-testid` spread onto the picker never reached the
   * DOM. It is declared here and routed through the one sanctioned channel
   * (`slotProps.textField.inputProps`, see `withInputTestId`), per the frontend rule: "If a wrapper
   * doesn't forward props to the DOM, add `'data-testid'?: string` to its
   * props and pass it through."
   */
  'data-testid'?: string;
}

const toPickerValue = (value: unknown): Date | null =>
  isCivilDate(value) ? fromCivilDate(value) : null;

export const FormCivilDatePicker = ({
  'data-testid': testId,
  name,
  ...props
}: FormCivilDatePickerProps) => {
  const slotProps = withInputTestId(props.slotProps, testId);
  return (
    <Controller
      render={({ field, formState: { defaultValues, errors } }) => {
        const formDefaultValue = toPickerValue(get(defaultValues, name));
        const error = get<FieldError>(errors, name) ?? undefined;
        return (
          <DatePicker
            {...field}
            defaultValue={formDefaultValue as unknown as Date}
            value={toPickerValue(field.value) as unknown as Date}
            onChange={(date: Date | null) => {
              // While typing, MUI emits partial input as an Invalid Date —
              // store null so validation reports "required", never a
              // 'NaN-NaN-NaN' string.
              field.onChange(
                date && !Number.isNaN(date.getTime())
                  ? toCivilDate(date)
                  : null,
              );
            }}
            fullWidth={props.fullWidth}
            errors={error?.message}
            name={name}
            {...props}
            slotProps={slotProps}
          />
        );
      }}
      name={name}
    />
  );
};

export default FormCivilDatePicker;
